import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  WBBox,
  WBButton,
  WBLink,
  WBSelect,
  WBTextField,
  useSnackbar,
} from '@admiin-com/ds-web';
import { MenuItem } from '@mui/material';
import {
  EntityClientsStatus,
  getEntity,
  updateEntity,
  type Entity as EntityType,
} from '@admiin-com/ds-graphql';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { PATHS } from '../../navigation/paths';

const schema = z.object({
  clientsStatus: z.nativeEnum(EntityClientsStatus),
});

const Entity = () => {
  const { t } = useTranslation('entities');
  const { id } = useParams();

  const { data } = useQuery(gql(getEntity), { variables: { id } });
  const [mutate, { loading }] = useMutation(gql(updateEntity));
  const showSnackbar = useSnackbar();
  const entity = data?.getEntity as EntityType | undefined;
  const canUpdate = entity?.clientsStatus !== EntityClientsStatus.DISABLED;
  const airwallexHost = import.meta.env.PROD
    ? 'https://www.airwallex.com'
    : 'https://demo.airwallex.com';

  const { control, handleSubmit, reset } = useForm<{
    clientsStatus: EntityClientsStatus;
  }>({
    defaultValues: { clientsStatus: EntityClientsStatus.REVIEW },
  });

  useEffect(() => {
    if (entity) {
      reset({ clientsStatus: entity.clientsStatus as EntityClientsStatus });
    }
  }, [entity, reset]);

  const onSubmit = async (values: { clientsStatus: EntityClientsStatus }) => {
    const parsed = schema.parse(values);
    try {
      await mutate({
        variables: { input: { id, clientsStatus: parsed.clientsStatus } },
      });
      showSnackbar({
        message: t('updateSuccess'),
        severity: 'success',
        horizontal: 'right',
        vertical: 'bottom',
      });
    } catch (err: any) {
      showSnackbar({
        message: err?.message ?? t('updateError'),
        severity: 'error',
        horizontal: 'right',
        vertical: 'bottom',
      });
    }
  };

  if (!entity) return null;

  return (
    <WBBox component="form" p={2} onSubmit={handleSubmit(onSubmit)}>
      <WBTextField
        label={t('id')}
        value={entity.id}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('name')}
        value={entity.name}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('taxNumber')}
        value={entity.taxNumber}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('type')}
        value={entity.type}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('companyNumber')}
        value={entity.companyNumber ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('legalName')}
        value={entity.legalName ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('businessNames')}
        value={(entity.businessNames ?? []).join(', ')}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('contactFirstName')}
        value={entity.contact?.firstName ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('contactLastName')}
        value={entity.contact?.lastName ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('contactEmail')}
        value={entity.contact?.email ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('contactPhone')}
        value={entity.contact?.phone ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('address1')}
        value={entity.address?.address1 ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('address2')}
        value={entity.address?.address2 ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('city')}
        value={entity.address?.city ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('state')}
        value={entity.address?.state ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('postalCode')}
        value={entity.address?.postalCode ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('website')}
        value={entity.website ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      {entity.connectedAccount && (
        <WBTextField
          label={t('connectedAccount')}
          value={`${airwallexHost}/app/platform-console/connected-accounts/list?id=${entity.connectedAccount}`}
          disabled
          fullWidth
          margin="normal"
        />
      )}
      <WBTextField
        label={t('accountStatus')}
        value={entity.accountStatus ?? ''}
        disabled
        fullWidth
        margin="normal"
      />
      <WBTextField
        label={t('isFirm')}
        value={entity.isFirm ? t('yes') : t('no')}
        disabled
        fullWidth
        margin="normal"
      />
      {entity.createdByEntityId && (
        <WBLink
          to={PATHS.entity(entity.createdByEntityId)}
          sx={{ display: 'block', mt: 2 }}
        >
          {t('createdByEntityId')}: {entity.createdByEntityId}
        </WBLink>
      )}
      <Controller
        name="clientsStatus"
        control={control}
        render={({ field }) => (
          <WBSelect
            label={t('clientsStatus') as string}
            fullWidth
            disabled={!canUpdate}
            {...field}
          >
            {Object.values(EntityClientsStatus).map((status) => (
              <MenuItem key={status} value={status}>
                {t(status)}
              </MenuItem>
            ))}
          </WBSelect>
        )}
      />
      {canUpdate && (
        <WBButton type="submit" disabled={loading} sx={{ mt: 2 }}>
          {t('update')}
        </WBButton>
      )}
    </WBBox>
  );
};

export default Entity;
